import { Component, type ErrorInfo, type ReactNode } from 'react';

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /** 어느 화면에서 났는지. 콘솔 로그와 안내 문구에 쓴다. */
  label: string;
}

interface RouteErrorBoundaryState {
  message: string | null;
}

/**
 * 라우트 하나를 감싸는 오류 경계.
 *
 * ── 왜 필요한가 ─────────────────────────────────────────────
 * 경계가 하나도 없으면 렌더 중 예외 하나에 **앱 전체가 언마운트되어 흰 화면**이
 * 된다. 사용자에게는 "데이터가 안 나온다"로 보이고, 콘솔의 `Uncaught TypeError`
 * 한 줄과 그 증상이 같은 사건이라는 걸 알아볼 방법이 없다.
 *
 * 경계는 **원인을 고치지 않는다.** 고장을 한 화면 안에 가두고, 무엇이
 * 터졌는지를 화면에 적어 다음 사람이 찾을 수 있게 할 뿐이다.
 *
 * 클래스 컴포넌트인 이유: React 는 아직 훅으로 오류를 잡는 방법을 주지 않는다.
 * 여기가 이 코드베이스에서 클래스가 정당한 유일한 자리다.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return {
      message: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 컴포넌트 스택이 있어야 어느 컴포넌트가 터졌는지 알 수 있다.
    // 브라우저 콘솔의 `Uncaught` 한 줄에는 그게 안 실린다.
    console.error(`[${this.props.label}] 렌더 오류`, error, info.componentStack);
  }

  render() {
    if (this.state.message === null) return this.props.children;

    return (
      <div className="p-8" role="alert">
        <h2 className="text-[16px] font-bold text-neutral-900 dark:text-neutral-100">
          화면을 그리는 중 오류가 났습니다.
        </h2>
        <p className="mt-1.5 text-[13.5px] text-neutral-500">
          {this.props.label} — 새로고침해도 같으면 아래 내용을 그대로 전달해
          주세요.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-xl border border-neutral-200 px-4 py-3 text-[12.5px] text-danger dark:border-white/12">
          {this.state.message}
        </pre>
      </div>
    );
  }
}
